/**
 * Score and history (F4), and the end of the game (ADR-0061).
 *
 * The same screen serves both: during play it shows the scores of the civilisations you
 * have met and how they have moved; when the game ends it says who won and how, and
 * shows everyone. Charts are small multiples — score, land, technologies — one measure
 * and one axis each, every civilisation in its own map colour with its own dash, named
 * at the end of its line and in the legend, so identity never rests on colour alone.
 * Hovering a chart reads every line at that turn.
 */

import type { Engine } from '../core/engine.js';
import { scoreOf } from '../core/score.js';
import type { HistorySeries } from '../core/state/types.js';
import { metRivals } from './rivals-screen.js';

export interface ScoreScreen {
  open(final?: boolean): void;
  close(): void;
  readonly isOpen: boolean;
  refresh(): void;
}

export interface ScoreScreenOptions {
  getEngine: () => Engine;
  newGame: () => void;
  loadGame: () => void;
}

/** Dash patterns, one per civilisation in seat order, so lines differ in more than colour. */
const DASHES = ['', '7 4', '2 3', '9 3 2 3', '1 5', '12 4'];

const CHARTS: Array<{ key: keyof HistorySeries; title: string }> = [
  { key: 'score', title: 'Score' },
  { key: 'land', title: 'Land (tiles)' },
  { key: 'techs', title: 'Technologies' },
];

export function createScoreScreen(options: ScoreScreenOptions): ScoreScreen {
  const root = document.createElement('div');
  root.className = 'cities-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="cities-panel score-panel">
      <header>
        <h2 id="sc-title">Score</h2>
        <div class="cs-sub" id="sc-sub"></div>
        <button id="sc-close" title="Close (Esc)">Close</button>
      </header>
      <div class="cts-body">
        <table class="rv-table sc-table" id="sc-table"></table>
        <div class="sc-legend" id="sc-legend"></div>
        <div class="sc-charts" id="sc-charts"></div>
        <footer class="dialog-actions" id="sc-actions"></footer>
      </div>
    </div>`;
  document.body.append(root);
  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;
  let final = false;

  function refresh(): void {
    const engine = options.getEngine();
    const { state, content } = engine;
    const me = engine.activeCiv;
    const outcome = engine.outcome();
    const result = state.result;
    // During play, only the civilisations you have met; at the end, everyone.
    const shown = (final ? Object.keys(state.civs).map(Number) : [me, ...metRivals(engine)]).sort((a, b) => a - b);
    const seat = new Map(Object.keys(state.civs).map(Number).sort((a, b) => a - b).map((id, i) => [id, i]));

    if (final) {
      const winner = result ? state.civs[result.winner] : null;
      $('sc-title').textContent = outcome === 'victory' ? 'Victory' : 'Defeat';
      $('sc-sub').textContent =
        outcome === 'victory'
          ? `Every rival has fallen — victory by conquest on turn ${result?.turn ?? state.meta.turn}.`
          : winner
            ? `${winner.name} has conquered the world, on turn ${result?.turn}.`
            : `Your last city has fallen, on turn ${state.meta.turn}.`;
    } else {
      $('sc-title').textContent = 'Score';
      $('sc-sub').textContent = `Turn ${state.meta.turn} · scores are public; civilisations you have not met are not shown`;
    }

    // ── the table ───────────────────────────────────────────────────────────────
    const rows = shown
      .map((id) => ({ id, civ: state.civs[id]!, score: scoreOf(state, content, id) }))
      .sort((a, b) => b.score.total - a.score.total || a.id - b.id);
    const labels = rows[0]?.score.parts.map((p) => p.label).filter((l) => !l.startsWith('victory')) ?? [];
    $('sc-table').innerHTML =
      `<tr><th>Civilisation</th><th>Score</th>${labels.map((l) => `<th>${escapeHtml(l)}</th>`).join('')}<th>Victory</th></tr>` +
      rows
        .map(({ id, civ, score }) => {
          const cells = labels
            .map((label) => {
              const part = score.parts.find((p) => p.label === label)!;
              return `<td>${part.points} <span class="dim">(${part.count})</span></td>`;
            })
            .join('');
          const won = score.parts.find((p) => p.label.startsWith('victory'));
          return `<tr class="${id === me ? 'sc-me' : ''}"><td><span class="rv-swatch" style="background:${civ.colour}"></span><b>${escapeHtml(civ.name)}</b>${civ.defeated ? ' <span class="dim">destroyed</span>' : ''}</td>
            <td><b>${score.total}</b></td>${cells}<td>${won ? won.points : '—'}</td></tr>`;
        })
        .join('');

    // ── legend and charts ────────────────────────────────────────────────────────
    const series = shown.filter((id) => state.history.civs[id]);
    $('sc-legend').innerHTML = series
      .map((id) => {
        const civ = state.civs[id]!;
        return `<span class="sc-key"><svg width="26" height="10" aria-hidden="true"><line x1="1" y1="5" x2="25" y2="5" stroke="${civ.colour}" stroke-width="2" stroke-dasharray="${DASHES[(seat.get(id) ?? 0) % DASHES.length]}"/></svg>${escapeHtml(civ.name)}</span>`;
      })
      .join('');
    const turns = state.history.turns;
    if (turns.length < 2) {
      $('sc-charts').innerHTML = '<div class="dim">History is recorded every few turns — come back a little later.</div>';
    } else {
      $('sc-charts').innerHTML = CHARTS.map((c) => chart(c.key, c.title, series, seat)).join('');
      for (const svg of root.querySelectorAll<SVGSVGElement>('svg.sc-chart')) wireHover(svg, series);
    }

    $('sc-actions').innerHTML = final
      ? '<button id="sc-more" class="primary">One more turn</button><button id="sc-new">New game</button><button id="sc-load">Load a save</button>'
      : '';
    if (final) {
      $('sc-more').addEventListener('click', close);
      $('sc-new').addEventListener('click', () => {
        close();
        options.newGame();
      });
      $('sc-load').addEventListener('click', () => {
        close();
        options.loadGame();
      });
    }
  }

  // Chart geometry, in SVG units.
  const W = 300;
  const H = 170;
  const PAD = { left: 38, right: 62, top: 12, bottom: 22 };

  function chart(key: keyof HistorySeries, title: string, ids: number[], seat: Map<number, number>): string {
    const { state } = options.getEngine();
    const turns = state.history.turns;
    const first = turns[0]!;
    const last = turns[turns.length - 1]!;
    let max = 1;
    for (const id of ids) for (const v of state.history.civs[id]![key]) max = Math.max(max, v);
    const nice = niceCeiling(max);
    const x = (t: number): number => PAD.left + ((t - first) / Math.max(1, last - first)) * (W - PAD.left - PAD.right);
    const y = (v: number): number => PAD.top + (1 - v / nice) * (H - PAD.top - PAD.bottom);

    const grid = [0, 0.5, 1]
      .map((f) => {
        const v = Math.round(nice * f);
        return `<line x1="${PAD.left}" x2="${W - PAD.right}" y1="${y(v)}" y2="${y(v)}" class="sc-grid"/><text x="${PAD.left - 6}" y="${y(v) + 3}" class="sc-axis" text-anchor="end">${v}</text>`;
      })
      .join('');
    const xTicks = [first, Math.round((first + last) / 2), last]
      .map((t) => `<text x="${x(t)}" y="${H - 6}" class="sc-axis" text-anchor="middle">${t}</text>`)
      .join('');

    // Lines, then names at their ends, nudged apart so they never sit on one another.
    const ends: Array<{ id: number; y: number }> = [];
    const lines = ids
      .map((id) => {
        const civ = state.civs[id]!;
        const values = state.history.civs[id]![key];
        const points = values.map((v, i) => `${x(turns[i]!).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
        ends.push({ id, y: y(values[values.length - 1] ?? 0) });
        return `<polyline points="${points}" fill="none" stroke="${civ.colour}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" stroke-dasharray="${DASHES[(seat.get(id) ?? 0) % DASHES.length]}"/>`;
      })
      .join('');
    ends.sort((a, b) => a.y - b.y);
    for (let i = 1; i < ends.length; i++) ends[i]!.y = Math.max(ends[i]!.y, ends[i - 1]!.y + 11);
    const names = ends
      .map((e) => `<text x="${W - PAD.right + 6}" y="${e.y + 3}" class="sc-name">${escapeHtml(state.civs[e.id]!.name)}</text>`)
      .join('');

    return `<figure class="sc-figure"><figcaption>${escapeHtml(title)}</figcaption>
      <svg class="sc-chart" viewBox="0 0 ${W} ${H}" data-key="${key}" role="img" aria-label="${escapeHtml(title)} over time">
        ${grid}${xTicks}${lines}${names}
        <line class="sc-cross" x1="0" x2="0" y1="${PAD.top}" y2="${H - PAD.bottom}" visibility="hidden"/>
        <rect x="${PAD.left}" y="0" width="${W - PAD.left - PAD.right}" height="${H}" fill="transparent" class="sc-hit"/>
      </svg><div class="sc-tip" hidden></div></figure>`;
  }

  function wireHover(svg: SVGSVGElement, ids: number[]): void {
    const { state } = options.getEngine();
    const turns = state.history.turns;
    const key = svg.dataset['key'] as keyof HistorySeries;
    const cross = svg.querySelector<SVGLineElement>('.sc-cross')!;
    const tip = svg.parentElement!.querySelector<HTMLDivElement>('.sc-tip')!;
    const first = turns[0]!;
    const last = turns[turns.length - 1]!;
    svg.addEventListener('mousemove', (event) => {
      const box = svg.getBoundingClientRect();
      const sx = ((event.clientX - box.left) / box.width) * W;
      const t = first + ((sx - PAD.left) / (W - PAD.left - PAD.right)) * (last - first);
      let i = 0;
      for (let k = 1; k < turns.length; k++) if (Math.abs(turns[k]! - t) < Math.abs(turns[i]! - t)) i = k;
      const px = PAD.left + ((turns[i]! - first) / Math.max(1, last - first)) * (W - PAD.left - PAD.right);
      cross.setAttribute('x1', String(px));
      cross.setAttribute('x2', String(px));
      cross.setAttribute('visibility', 'visible');
      const lines = ids
        .map((id) => ({ id, v: state.history.civs[id]![key][i] ?? 0 }))
        .sort((a, b) => b.v - a.v)
        .map(({ id, v }) => `<div><span class="rv-swatch" style="background:${state.civs[id]!.colour}"></span>${escapeHtml(state.civs[id]!.name)} <b>${v}</b></div>`)
        .join('');
      tip.innerHTML = `<div class="dim">Turn ${turns[i]}</div>${lines}`;
      tip.hidden = false;
      tip.style.left = `${Math.min(box.width - 150, Math.max(0, (px / W) * box.width + 10))}px`;
    });
    svg.addEventListener('mouseleave', () => {
      cross.setAttribute('visibility', 'hidden');
      tip.hidden = true;
    });
  }

  function open(isFinal = false): void {
    final = isFinal;
    document.body.append(root);
    root.hidden = false;
    refresh();
  }
  function close(): void {
    root.hidden = true;
  }
  $('sc-close').addEventListener('click', close);
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });
  return {
    open,
    close,
    get isOpen(): boolean {
      return !root.hidden;
    },
    refresh,
  };
}

/** A round number at or above `v`, for an axis that reads cleanly. */
function niceCeiling(v: number): number {
  const step = 10 ** Math.floor(Math.log10(Math.max(1, v)));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * step >= v) return m * step;
  return 10 * step;
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
